import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { UnifiedStream, UserInfo, StreamsResponse } from '../types';
import { getLiveStreams } from '../api';
import { StreamCard, StreamCardSkeleton } from './StreamCard';
import { TwitchIcon, YouTubeIcon, StreamIcon, RefreshIcon, SearchIcon, SettingsIcon } from './Icons';
import { Settings, loadSettings, applyTheme } from './Settings';
import { useToast } from './Toast';

interface DashboardProps {
  userInfo: UserInfo;
}

type PlatformFilter = 'all' | 'twitch' | 'youtube';
type SortOption = 'viewers-desc' | 'viewers-asc' | 'name-asc' | 'name-desc' | 'recent';

export const Dashboard: React.FC<DashboardProps> = ({ userInfo }) => {
  const [streams, setStreams] = useState<UnifiedStream[]>([]);
  const [platforms, setPlatforms] = useState({ twitch: false, youtube: false });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<PlatformFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('viewers-desc');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  const { addToast } = useToast();

  // Apply theme on mount
  useEffect(() => {
    const settings = loadSettings();
    applyTheme(settings.theme);
  }, []);

  const fetchStreams = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      setError(null);
      const data: StreamsResponse = await getLiveStreams();
      setStreams(data.streams);
      setPlatforms(data.platforms);
      setLastRefresh(new Date());
      
      if (showRefreshing && data.streams.length > 0) {
        addToast({
          type: 'success',
          title: 'Streams updated',
          message: `Found ${data.streams.length} live stream${data.streams.length === 1 ? '' : 's'}`,
          duration: 3000,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch streams';
      setError(message);
      addToast({
        type: 'error',
        title: 'Error loading streams',
        message,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchStreams();
    
    // Auto-refresh based on settings
    const settings = loadSettings();
    if (settings.autoRefresh) {
      const interval = setInterval(() => fetchStreams(), settings.refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [fetchStreams]);

  // Filter and sort streams
  const filteredAndSortedStreams = useMemo(() => {
    let result = [...streams];
    
    // Platform filter
    if (filter !== 'all') {
      result = result.filter((stream) => stream.platform === filter);
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (stream) =>
          stream.title.toLowerCase().includes(query) ||
          stream.streamerName.toLowerCase().includes(query)
      );
    }
    
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'viewers-desc':
          return b.viewerCount - a.viewerCount;
        case 'viewers-asc':
          return a.viewerCount - b.viewerCount;
        case 'name-asc':
          return a.streamerName.localeCompare(b.streamerName);
        case 'name-desc':
          return b.streamerName.localeCompare(a.streamerName);
        case 'recent':
          return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
        default:
          return 0;
      }
    });
    
    return result;
  }, [streams, filter, searchQuery, sortBy]);

  const twitchConnected = userInfo.connections.twitch.connected;
  const youtubeConnected = userInfo.connections.youtube.connected;
  
  const twitchCount = streams.filter(s => s.platform === 'twitch').length;
  const youtubeCount = streams.filter(s => s.platform === 'youtube').length;

  const formatLastRefresh = () => {
    const seconds = Math.floor((Date.now() - lastRefresh.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  const handleManualRefresh = () => {
    fetchStreams(true);
  };

  if (loading) {
    return (
      <div className="container dashboard">
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h1>Live Streams</h1>
            <span className="stream-count">Loading...</span>
          </div>
        </div>
        <div className="stream-grid">
          {[...Array(6)].map((_, i) => (
            <StreamCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container dashboard">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h1>Live Streams</h1>
          <span className="stream-count">
            {filteredAndSortedStreams.length} of {streams.length} streams
          </span>
        </div>
        
        <div className="header-actions">
          <button
            className={`btn btn-outline refresh-indicator ${refreshing ? 'refreshing' : ''}`}
            onClick={handleManualRefresh}
            disabled={refreshing}
            title="Refresh streams"
          >
            <RefreshIcon />
            <span>{refreshing ? 'Refreshing...' : formatLastRefresh()}</span>
          </button>
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => setSettingsOpen(true)}
            title="Settings"
          >
            <SettingsIcon />
          </button>
        </div>
      </div>

      <div className="connections">
        <div className={`connection-badge ${twitchConnected ? 'connected' : 'disconnected'}`}>
          <span className="status-dot" />
          <TwitchIcon />
          <span>
            {twitchConnected 
              ? `${userInfo.connections.twitch.username || 'Connected'} (${twitchCount})`
              : 'Twitch Not Connected'
            }
          </span>
        </div>
        <div className={`connection-badge ${youtubeConnected ? 'connected' : 'disconnected'}`}>
          <span className="status-dot" />
          <YouTubeIcon />
          <span>
            {youtubeConnected 
              ? `${userInfo.connections.youtube.email || 'Connected'} (${youtubeCount})`
              : 'YouTube Not Connected'
            }
          </span>
        </div>
      </div>

      <div className="controls-bar">
        <div className="search-box">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search streams or streamers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search streams"
          />
        </div>
        
        <div className="controls-divider" />
        
        <div className="filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({streams.length})
          </button>
          <button
            className={`filter-btn twitch ${filter === 'twitch' ? 'active' : ''}`}
            onClick={() => setFilter('twitch')}
            disabled={!platforms.twitch}
          >
            <TwitchIcon />
            Twitch ({twitchCount})
          </button>
          <button
            className={`filter-btn youtube ${filter === 'youtube' ? 'active' : ''}`}
            onClick={() => setFilter('youtube')}
            disabled={!platforms.youtube}
          >
            <YouTubeIcon />
            YouTube ({youtubeCount})
          </button>
        </div>
        
        <div className="controls-divider" />
        
        <select
          className="sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          aria-label="Sort streams"
        >
          <option value="viewers-desc">Most viewers</option>
          <option value="viewers-asc">Least viewers</option>
          <option value="recent">Recently started</option>
          <option value="name-asc">Name A-Z</option>
          <option value="name-desc">Name Z-A</option>
        </select>
      </div>

      {error && (
        <div className="error">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => fetchStreams()}>
            Try Again
          </button>
        </div>
      )}

      {!error && filteredAndSortedStreams.length === 0 && streams.length > 0 && (
        <div className="empty-state">
          <SearchIcon />
          <h3>No matching streams</h3>
          <p>Try adjusting your search or filters to find what you're looking for.</p>
        </div>
      )}

      {!error && streams.length === 0 && (
        <div className="empty-state">
          <StreamIcon />
          <h3>No Live Streams</h3>
          <p>
            {!twitchConnected && !youtubeConnected
              ? 'Connect your Twitch or YouTube account to see live streams from channels you follow.'
              : 'None of the channels you follow are currently live. Check back later!'}
          </p>
        </div>
      )}

      {filteredAndSortedStreams.length > 0 && (
        <div className="stream-grid">
          {filteredAndSortedStreams.map((stream) => (
            <StreamCard key={stream.id} stream={stream} />
          ))}
        </div>
      )}

      <Settings isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
};
